import React, {useEffect, useState, useRef} from "react";
import {useObserver} from "mobx-react-lite";
import {Col, Row, Form, Input, Switch, Tooltip, Button, Modal, message, Select} from "antd";
import {BankOutlined, InfoCircleOutlined} from "@ant-design/icons";
import MaskedInput from "antd-mask-input";
import {useFormik, FormikProvider, getIn} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";
import * as Sentry from "@sentry/react";
import {SingleMarkerMap} from "../../_defaultComponents/SingleMarkerMap/SingleMarkerMap";
import {updateSubCompanyValidation} from "../updateSubCompany/updateSubCompanyValidation";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {useSubCompanyLocationSync} from "../../../hooks/useSubCompanyLocationSync";
import TagInput from "../../_defaultComponents/TagInput/TagInput";
import {AuthorizedIDForm} from "../../profile/AuthorizedIDForm/AuthorizedIDForm";
import {SettingsContainer, SwitchContainer, LocationFieldsHint} from "./UpdateSubCompanyStyles";
import {ZoneSelect} from "components/_defaultComponents/ZoneSelect";
import {CreditModeSelect} from "../../_defaultComponents/CreditModeSelect";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import DistrictFilter from "../../_defaultComponents/FilterComponents/DistrictFilter";
import DynamicNeighborhoodFilter from "../../_defaultComponents/FilterComponents/DynamicNeighborhoodFilter";
import {buildSubCompanyCreatePayload, buildSubCompanyUpdatePayload} from "../subCompanyPayload";
import {
    invoiceCutPeriodOptions,
    isInvoicePeriodDisabled,
    isInvoicePeriodRequired,
    managerCompanyOptions
} from "../../../constants/constants";

export const UpdateSubCompany = ({subCompanyId}) => {
    const history = useHistory();
    const stores = useStore();
    const {t} = useTranslation();
    const [companyCode, setCompanyCode] = useState();
    const [companyCityCode, setCompanyCityCode] = useState();
    const [tags, setTags] = useState([]);
    const [poolSwitch, setPoolSwitch] = useState(false);
    const [smartLockerSwitch, setSmartLockerSwitch] = useState(false);
    const [isAllocateClosed, setIsAllocateClosed] = useState(false);
    const [onboardingStatus, setOnboardingStatus] = useState("");
    const [isTestUser, setIsTestUser] = useState(false);
    const [isPoolIntensityAlertActive, setIsPoolIntensityAlertActive] = useState(false);
    const [addReturnProduct, setAddReturnProduct] = useState(false);
    const [jokerLeadIsActive, setJokerLeadIsActive] = useState(false);
    const [jokerLeadThreshold, setJokerLeadThreshold] = useState(0);
    const [initialWorkType, setInitialWorkType] = useState(null);
    const [showWorkTypeChangeModal, setShowWorkTypeChangeModal] = useState(false);
    const [pendingUpdateValues, setPendingUpdateValues] = useState(null);
    const [districtId, setDistrictId] = useState(null);
    const {
        resolveAddressFromLocation,
        resolveDistrictId,
        resolveLocationFromAddress,
        isResolving
    } = useSubCompanyLocationSync();
    // Forward geocode YALNIZCA kullanıcı il/ilçe/mahalle'yi elle seçtiğinde çalışsın diye
    // açık bir "izin" bayrağı kullanılıyor (varsayılan false). Böylece hem kayıt açılırken
    // formik'in mevcut değerlerle yeniden başlatılması hem de haritadan gelen programatik
    // doldurma sırasında efekt sessizce atlanır; yalnız CityFilter/DistrictFilter/
    // DynamicNeighborhoodFilter onChange'leri bayrağı true yapar.
    const allowForwardGeocodeRef = useRef(false);
    const isManualAddressRef = useRef(false);
    const [previewLocation, setPreviewLocation] = useState(null);

    const [loading, subCompany, setSubCompany, setLoading] = useFetch({
        fetchFn: stores.companyStore.getSubCompany.bind(null, {subCompanyId}),
        showLoading: true,
        condition: !!subCompanyId,
        callbackFunc: response => {
            if (response?.data?.isSmartLocker) {
                setSmartLockerSwitch(true);
            }
            if (response?.data?.onboardingStatus) {
                setOnboardingStatus(response.data.onboardingStatus);
            }
            if (response?.data?.isTestUser) {
                setIsTestUser(response.data.isTestUser);
            }
            if (response?.data?.isPoolIntensityAlertActive) {
                setIsPoolIntensityAlertActive(response.data.isPoolIntensityAlertActive);
            }
            if (response?.data?.options?.codPayment?.addReturnProduct) {
                setAddReturnProduct(response.data.options.codPayment.addReturnProduct);
            }
            if (response?.data?.options?.jokerLeadOrder) {
                setJokerLeadIsActive(!!response.data.options.jokerLeadOrder.isActive);
                setJokerLeadThreshold(response.data.options.jokerLeadOrder.thresholdMinutes || 0);
            }
        }
    });

    const [contractsLoading, paymentContractsData] = useFetch({
        fetchFn: () => stores.companyStore.getSubCompanyPaymentContracts({subCompanyId}),
        showLoading: false,
        condition: !!subCompanyId,
        defaultDataValue: null
    });

    /*TODO: Form component will be used in UpdateSubCompany and AddSubCompany pages.
    Code refactoring needed for blocking duplicate usage.
    */

    const formik = useFormik({
        initialValues: {
            ...subCompany?.data,
            uniqueKey: subCompany?.data?.uniqueKey || "",
            subCompanyCode: subCompany?.data?.subCompanyCode?.code,
            expCode: subCompany?.data?.expCode,
            onboardingStatus: subCompany?.data?.onboardingStatus,
            isAllocateClosed: subCompany?.data?.isAllocateClosed,
            collectionAmount: subCompany?.data?.collectionAmount,
            maxAcceptableDistance: subCompany?.data?.maxAcceptableDistance,
            category: subCompany?.data?.category,
            signName: subCompany?.data?.signName,
            externalId: subCompany?.data?.externalId,
            isTestUser: subCompany?.data?.isTestUser,
            creditMode: subCompany?.data?.creditMode,
            invoicePeriod: subCompany?.data?.invoicePeriod,
            managerCompany: subCompany?.data?.managerCompany,
            zoneId: subCompany?.data?.zoneInfo?.zoneId,
            city: subCompany?.data?.city,
            county: subCompany?.data?.county?.toLocaleLowerCase("tr-TR"),
            district: subCompany?.data?.district?.toLocaleLowerCase("tr-TR"),
            options: subCompany?.data?.options || {}
        },
        validationSchema: updateSubCompanyValidation,
        enableReinitialize: true,
        onSubmit: async values => {
            values.authorizedPhone = values.authorizedPhone.replace(/[-,(,)]/g, "");
            values.subCompanyCode = `${companyCode}${("00" + companyCityCode).slice(-2)}${
                values.subCompanyCode
            }`;
            values.onboardingStatus = onboardingStatus;
            values.isAllocateClosed = isAllocateClosed;
            values.isTestUser = isTestUser;
            if (!values.invoicePeriod || isInvoicePeriodDisabled(values.creditMode)) {
                if (subCompany?.data?.invoicePeriod) values.invoicePeriod = null;
                else delete values.invoicePeriod;
            }
            values.subCompanyWorkType = poolSwitch ? "pool" : "dedicated";
            if (smartLockerSwitch) {
                values.isSmartLocker = true;
            } else {
                values.isSmartLocker = false;
            }
            if (isPoolIntensityAlertActive) {
                values.isPoolIntensityAlertActive = true;
            } else {
                values.isPoolIntensityAlertActive = false;
            }
            if (poolSwitch) {
                values.subCompanyWorkType = "pool";
            } else {
                values.subCompanyWorkType = "dedicated";
            }
            if (!values.options) {
                values.options = {};
            }
            if (!values.options.codPayment) {
                values.options.codPayment = {};
            }
            if (addReturnProduct) {
                values.options.codPayment.addReturnProduct = true;
            } else {
                values.options.codPayment.addReturnProduct = false;
            }
            values.options.jokerLeadOrder = {
                isActive: jokerLeadIsActive,
                thresholdMinutes: Number(jokerLeadThreshold) || 0
            };

            await handleSubCompanySubmit(values);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        setValues,
        setSubmitting,
        submitCount
    } = formik;

    const handleCreditModeChange = value => {
        setValues({
            ...values,
            creditMode: value,
            ...(isInvoicePeriodDisabled(value) && {invoicePeriod: null})
        });
    };

    const handleSubCompanySubmit = async values => {
        const payload = buildSubCompanyUpdatePayload(values, subCompany?.data);
        const currentWorkType = poolSwitch ? "pool" : "dedicated";
        const workTypeChanged = initialWorkType && initialWorkType !== currentWorkType;

        // Check if there's an active or future contract
        const hasActiveContract =
            paymentContractsData?.paymentContracts?.some(contract =>
                ["active", "future"].includes(contract?.activityStatus)
            ) || false;

        // If work type changed and there's an active contract, show confirmation
        if (workTypeChanged && hasActiveContract) {
            setPendingUpdateValues(payload);
            setShowWorkTypeChangeModal(true);
        } else {
            // Normal update flow
            const result = await stores.companyStore.updateSubCompany(payload);
            if (result) {
                history.push("/companies/sub-company/" + subCompanyId);
            }
        }
    };

    const handleConfirmWorkTypeChange = async () => {
        if (pendingUpdateValues) {
            setSubmitting(true);
            try {
                const result = await stores.companyStore.updateSubCompany(pendingUpdateValues);
                setShowWorkTypeChangeModal(false);
                setPendingUpdateValues(null);
                if (result) {
                    history.push(`/sub-company/payment-contract/${subCompanyId}`);
                }
            } catch (error) {
                // Error handling
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleCancelWorkTypeChange = () => {
        setShowWorkTypeChangeModal(false);
        setPendingUpdateValues(null);
    };

    useEffect(() => {
        if (values.companyId && subCompany?.data?._id && !subCompany?.data?.subCompanyCode?.code) {
            stores.companyStore
                .generateSubcompanyCode({
                    companyId: values.companyId,
                    subCompanyLocation: values.location
                })
                .then(res => {
                    setCompanyCode(res.companyCode);
                    setCompanyCityCode(res.cityCode);
                    setFieldValue("subCompanyCode", ("0000" + res.code).slice(-4));
                })
                .catch(e => Sentry.captureException(e));
        } else if (subCompany?.data?.subCompanyCode?.code) {
            setCompanyCode(subCompany.data.subCompanyCode.companyCode);
            setCompanyCityCode(subCompany.data.subCompanyCode.cityCode);
            setFieldValue(
                "subCompanyCode",
                ("0000" + subCompany.data.subCompanyCode.code).slice(-4)
            );
        }
        if (subCompany.data?.allowedOrderProviders) {
            setTags(subCompany.data?.allowedOrderProviders);
        }
        if (subCompany?.data?.onboardingStatus) {
            setOnboardingStatus(subCompany.data.onboardingStatus);
        }
        if (subCompany?.data?.isAllocateClosed) {
            setIsAllocateClosed(subCompany.data.isAllocateClosed);
        } else {
            setIsAllocateClosed(false);
        }
        if (subCompany?.data?.isTestUser) {
            setIsTestUser(subCompany.data.isTestUser);
        } else {
            setIsTestUser(false);
        }
        if (subCompany?.data?.isPoolIntensityAlertActive) {
            setIsPoolIntensityAlertActive(subCompany.data.isPoolIntensityAlertActive);
        } else {
            setIsPoolIntensityAlertActive(false);
        }
        // Set pool switch based on work type
        if (subCompany?.data?.subCompanyWorkType === "pool") {
            setPoolSwitch(true);
        } else {
            setPoolSwitch(false);
        }
        // Set initial work type
        if (subCompany?.data?.subCompanyWorkType) {
            setInitialWorkType(subCompany?.data?.subCompanyWorkType);
        }
    }, [
        values.companyId,
        subCompany?.data?._id,
        subCompany?.data?.subCompanyCode?.code,
        subCompany?.data?.onboardingStatus,
        subCompany?.data?.isAllocateClosed,
        subCompany?.data?.isTestUser,
        subCompany?.data?.isPoolIntensityAlertActive,
        values?.location?.longitude,
        values?.location?.latitude,
        subCompany?.data?.subCompanyWorkType
    ]);

    useEffect(() => {
        setFieldValue("allowedOrderProviders", tags);
    }, [tags]);

    // İl/ilçe/mahalle elle seçildiğinde marker'ı en spesifik seçime taşır — yalnız il
    // seçildiğinde il merkezine, ilçe de seçilince ilçe merkezine, mahalle de seçilince
    // mahalle merkezine (bkz. AddSubCompany.js'teki aynı desen). Yalnızca kullanıcı Filter
    // component'lerinden birini değiştirdiğinde (allowForwardGeocodeRef) tetiklenir —
    // kayıt açılırken mevcut değerlerle yeniden başlatılma sırasında atlanır.
    useEffect(() => {
        if (!allowForwardGeocodeRef.current) return;
        allowForwardGeocodeRef.current = false;
        if (!values.city) return;

        const timer = setTimeout(async () => {
            const location = await resolveLocationFromAddress({
                city: values.city,
                county: values.county,
                district: values.district
            });
            if (!location) {
                message.warning("Seçilen konum için harita güncellenemedi.");
                return;
            }
            setPreviewLocation({
                lat: location.latitude,
                lng: location.longitude,
                zoom: values.district ? 15 : values.county ? 13 : 11
            });
        }, 800);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.city, values.county, values.district]);

    useEffect(() => {
        if (!subCompany?.data?.city || !subCompany?.data?.county) return;
        resolveDistrictId({
            city: subCompany.data.city,
            county: subCompany.data.county?.toLocaleLowerCase("tr-TR")
        }).then(setDistrictId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subCompany?.data?._id]);

    // Google linki (veya elle seçim yokken harita tıklaması): konum ve il/ilçe/mahalle
    // baştan OSM'den doldurulur.
    const handleLocationFromLink = async ({lat, lng}) => {
        isManualAddressRef.current = false;
        setPreviewLocation(null);
        // Harita tıklaması zaten konumun kaynağı; forward geocode
        // efektinin bu programatik doldurmayı tekrar işlemesini önle.
        allowForwardGeocodeRef.current = false;
        setFieldValue(`location`, {longitude: lng, latitude: lat});
        setFieldValue("zoneId", null);
        // Yeni sonuç gelene kadar eski (yanlış) il/ilçe/mahalle kalmasın
        setFieldValue("city", "");
        setFieldValue("county", "");
        setFieldValue("district", "");
        setDistrictId(null);

        const resolved = await resolveAddressFromLocation({lat, lng});
        setFieldValue("city", resolved.city);
        setFieldValue("county", resolved.county);
        setFieldValue("district", resolved.district);
        setDistrictId(resolved.districtId);

        if (resolved.matchedLevel === "none") {
            message.warning(
                "Seçilen konum için adres bilgisi alınamadı, il/ilçe/mahalle alanlarını elle seçebilirsiniz."
            );
        } else if (resolved.matchedLevel === "city") {
            message.warning(
                "Konumun ilçesi kayıtlarımızda bulunamadı, ilçe ve mahalleyi elle seçiniz."
            );
        } else if (resolved.matchedLevel === "county") {
            message.warning("Konumun mahallesi kayıtlarımızda bulunamadı, mahalleyi elle seçiniz.");
        }
    };

    // Elle seçilmiş adres varken haritaya tıklanırsa yalnızca pin taşınır; seçim korunur.
    const handleMapClick = ({lat, lng}) => {
        if (!isManualAddressRef.current) {
            handleLocationFromLink({lat, lng});
            return;
        }
        setFieldValue("location", {longitude: lng, latitude: lat});
        setFieldValue("zoneId", null);
        setPreviewLocation(null);
    };

    return useObserver(() => (
        <Col>
            <Form
                autoComplete="off"
                labelCol={{span: 24}}
                wrapperCol={{span: 24}}
                onFinish={handleSubmit}
            >
                <ErrorFocus formik={formik} />
                <FormikProvider value={formik}>
                    <Form.Item
                        label={`${t("fields.companyName", {
                            ns: "common"
                        })} / ${t("fields.branchName", {ns: "common"})}`}
                        validateStatus={
                            getIn(errors, `name`) && getIn(touched, `name`) ? "error" : "success"
                        }
                        help={
                            getIn(errors, `name`) && getIn(touched, `name`)
                                ? getIn(errors, `name`)
                                : null
                        }
                    >
                        <Input name={"name"} onChange={handleChange} value={values.name} />
                    </Form.Item>
                    <Form.Item label="Tabela Adı">
                        <Input name={"signName"} onChange={handleChange} value={values.signName} />
                    </Form.Item>
                    <Form.Item label="Firma Kategorisi">
                        <Input name={"category"} onChange={handleChange} value={values.category} />
                    </Form.Item>
                    <Form.Item
                        label="Tahsilat Miktarı"
                        validateStatus={
                            getIn(errors, `collectionAmount`) && getIn(touched, `collectionAmount`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `collectionAmount`) && getIn(touched, `collectionAmount`)
                                ? getIn(errors, `collectionAmount`)
                                : null
                        }
                    >
                        <Input
                            type="number"
                            name={"collectionAmount"}
                            onChange={e => {
                                const value = parseFloat(e.target.value);
                                if (value >= 0 || e.target.value === "") {
                                    handleChange(e);
                                }
                            }}
                            value={values.collectionAmount}
                            min={0}
                        />
                    </Form.Item>
                    {(stores?.meStore?.checkRole("isAdmin") ||
                        stores?.meStore?.checkRole("isOperationUser")) && (
                        <>
                            <Form.Item
                                label="Maksimum Mesafe"
                                validateStatus={
                                    getIn(errors, `maxAcceptableDistance`) &&
                                    getIn(touched, `maxAcceptableDistance`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `maxAcceptableDistance`) &&
                                    getIn(touched, `maxAcceptableDistance`)
                                        ? getIn(errors, `maxAcceptableDistance`)
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    name={"maxAcceptableDistance"}
                                    onChange={e => {
                                        const value = parseFloat(e.target.value);
                                        if (value >= 0 || e.target.value === "") {
                                            handleChange(e);
                                        }
                                    }}
                                    value={values.maxAcceptableDistance}
                                    min={0}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Zorunlu Kurye Bildirim Mesafesi"
                                validateStatus={
                                    getIn(errors, `forceCourierNotificationDistance`) &&
                                    getIn(touched, `forceCourierNotificationDistance`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `forceCourierNotificationDistance`) &&
                                    getIn(touched, `forceCourierNotificationDistance`)
                                        ? getIn(errors, `forceCourierNotificationDistance`)
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    name={"forceCourierNotificationDistance"}
                                    onChange={e => {
                                        const value = parseFloat(e.target.value);
                                        if (value >= 0 || e.target.value === "") {
                                            handleChange(e);
                                        }
                                    }}
                                    value={values.forceCourierNotificationDistance}
                                    min={0}
                                />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item
                        label={t("fields.authorizedPerson", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `authorizedPerson`) && getIn(touched, `authorizedPerson`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `authorizedPerson`) && getIn(touched, `authorizedPerson`)
                                ? getIn(errors, `authorizedPerson`)
                                : null
                        }
                    >
                        <Input
                            name={"authorizedPerson"}
                            value={values.authorizedPerson}
                            onChange={handleChange}
                        />
                    </Form.Item>
                    <Row type="flex" justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("fields.authorizedPhone", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `authorizedPhone`) &&
                                    getIn(touched, `authorizedPhone`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `authorizedPhone`) &&
                                    getIn(touched, `authorizedPhone`)
                                        ? getIn(errors, `authorizedPhone`)
                                        : null
                                }
                            >
                                <MaskedInput
                                    placeholder="0(555)-555-5555"
                                    mask="1(111)-111-1111"
                                    name={"authorizedPhone"}
                                    value={values.authorizedPhone}
                                    onChange={handleChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("fields.authorizedEmail", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `authorizedEmail`) &&
                                    getIn(touched, `authorizedEmail`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `authorizedEmail`) &&
                                    getIn(touched, `authorizedEmail`)
                                        ? getIn(errors, `authorizedEmail`)
                                        : null
                                }
                            >
                                <Input
                                    name={"authorizedEmail"}
                                    onChange={handleChange}
                                    value={values.authorizedEmail}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row type="flex" justify="space-between">
                        <Col span="24">
                            <AuthorizedIDForm
                                subCompany={subCompany?.data}
                                loading={loading}
                                refetch={() =>
                                    history.push("/companies/sub-company/" + subCompanyId)
                                }
                                okButtonText={"Doğrula ve Kaydet"}
                            />
                        </Col>
                    </Row>
                    <Form.Item
                        label={t("fields.address", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `address`) && getIn(touched, `address`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `address`) && getIn(touched, `address`)
                                ? getIn(errors, `address`)
                                : null
                        }
                    >
                        <Input.TextArea
                            name={`address`}
                            value={values.address}
                            onChange={handleChange}
                        />
                    </Form.Item>

                    <Form.Item
                        label={t("fields.uniqueKey", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `uniqueKey`) && getIn(touched, `uniqueKey`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `uniqueKey`) && getIn(touched, `uniqueKey`)
                                ? getIn(errors, `uniqueKey`)
                                : null
                        }
                    >
                        <Input
                            name={"uniqueKey"}
                            onChange={handleChange}
                            value={values.uniqueKey}
                        />
                    </Form.Item>
                    {stores?.meStore?.checkRole("isAdmin") && (
                        <Form.Item
                            label={"Para Transfer Kodu"}
                            validateStatus={
                                getIn(errors, `expCode`) && getIn(touched, `expCode`)
                                    ? "error"
                                    : "success"
                            }
                            help={
                                getIn(errors, `expCode`) && getIn(touched, `expCode`)
                                    ? getIn(errors, `expCode`)
                                    : null
                            }
                        >
                            <Input
                                name={"expCode"}
                                onChange={handleChange}
                                value={values.expCode}
                            />
                        </Form.Item>
                    )}
                    <Form.Item label="External ID">
                        <Input
                            name={"externalId"}
                            onChange={handleChange}
                            value={values.externalId}
                        />
                    </Form.Item>
                    <Form.Item label="Bölge">
                        <ZoneSelect
                            showTitle={false}
                            showSyUser={true}
                            marginBottom="0px"
                            cityName={values.city}
                            value={values.zoneId}
                            onChange={value => setFieldValue("zoneId", value)}
                        />
                    </Form.Item>

                    <SettingsContainer>
                        <h4 style={{marginBottom: "20px", color: "#262626"}}>Ayarlar</h4>

                        <Row gutter={[24, 16]}>
                            <Col xs={24} sm={24} md={8}>
                                <SwitchContainer>
                                    <span style={{fontWeight: "500"}}>
                                        Alt Firma Çalışma Şekli:
                                    </span>
                                    <Button.Group>
                                        <Button
                                            type={!poolSwitch ? "primary" : "default"}
                                            size="small"
                                            onClick={() => {
                                                setPoolSwitch(false);
                                            }}
                                            style={{
                                                backgroundColor: !poolSwitch
                                                    ? "#1890ff"
                                                    : "#f5f5f5",
                                                borderColor: !poolSwitch ? "#1890ff" : "#d9d9d9",
                                                color: !poolSwitch ? "#fff" : "#000",
                                                fontWeight: !poolSwitch ? "500" : "400",
                                                borderTopLeftRadius: "4px",
                                                borderBottomLeftRadius: "4px"
                                            }}
                                        >
                                            Dedike
                                        </Button>
                                        <Button
                                            type={poolSwitch ? "primary" : "default"}
                                            size="small"
                                            onClick={() => {
                                                setPoolSwitch(true);
                                            }}
                                            style={{
                                                backgroundColor: poolSwitch ? "#1890ff" : "#f5f5f5",
                                                borderColor: poolSwitch ? "#1890ff" : "#d9d9d9",
                                                color: poolSwitch ? "#fff" : "#000",
                                                fontWeight: poolSwitch ? "500" : "400",
                                                borderTopRightRadius: "4px",
                                                borderBottomRightRadius: "4px"
                                            }}
                                        >
                                            Havuz
                                        </Button>
                                    </Button.Group>
                                </SwitchContainer>
                            </Col>

                            <Col xs={24} sm={24} md={8}>
                                <SwitchContainer>
                                    <span style={{fontWeight: "500"}}>Kredi Modu</span>
                                    <CreditModeSelect
                                        showTitle={false}
                                        marginBottom="0px"
                                        allowClear
                                        value={values.creditMode}
                                        onChange={handleCreditModeChange}
                                        style={{width: 180}}
                                    />
                                </SwitchContainer>
                            </Col>

                            <Col xs={24} sm={24} md={8}>
                                <SwitchContainer>
                                    <span style={{fontWeight: 500}}> Fatura Kesim Dönemi </span>
                                    <Form.Item
                                        style={{marginBottom: 0}}
                                        required={isInvoicePeriodRequired(values.creditMode)}
                                        validateStatus={
                                            getIn(errors, `invoicePeriod`) &&
                                            (getIn(touched, `invoicePeriod`) || submitCount > 0)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `invoicePeriod`) &&
                                            (getIn(touched, `invoicePeriod`) || submitCount > 0)
                                                ? getIn(errors, `invoicePeriod`)
                                                : null
                                        }
                                    >
                                        {/* ErrorFocus hataya [name] ile kaydırıyor; antd Select DOM'a name basmıyor */}
                                        <Tooltip
                                            title={
                                                isInvoicePeriodDisabled(values.creditMode)
                                                    ? "Kredi modu kredili seçildiğinde fatura dönemi seçimi yapılamamaktadır."
                                                    : ""
                                            }
                                        >
                                            <div name="invoicePeriod">
                                                <Select
                                                    placeholder="Seçiniz"
                                                    allowClear
                                                    disabled={isInvoicePeriodDisabled(
                                                        values.creditMode
                                                    )}
                                                    value={values.invoicePeriod}
                                                    onChange={value =>
                                                        setFieldValue(
                                                            "invoicePeriod",
                                                            value ?? null
                                                        )
                                                    }
                                                    onBlur={() =>
                                                        setFieldTouched("invoicePeriod", true)
                                                    }
                                                    style={{width: 180}}
                                                >
                                                    {invoiceCutPeriodOptions.map(item => (
                                                        <Select.Option
                                                            key={item.value}
                                                            value={item.value}
                                                        >
                                                            {item.label}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </div>
                                        </Tooltip>
                                    </Form.Item>
                                </SwitchContainer>
                            </Col>

                            <Col xs={24} sm={24} md={8}>
                                <SwitchContainer>
                                    <span style={{fontWeight: "500"}}>Yönetici Firma</span>
                                    <Select
                                        data-testid="managerCompanySelect"
                                        style={{width: 180}}
                                        value={values.managerCompany}
                                        onChange={value => setFieldValue("managerCompany", value)}
                                    >
                                        {managerCompanyOptions.map(item => (
                                            <Select.Option key={item.value} value={item.value}>
                                                {item.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </SwitchContainer>
                            </Col>

                            <Col xs={24} sm={24} md={8}>
                                <SwitchContainer>
                                    <span style={{fontWeight: "500"}}>Smart Locker</span>
                                    <Switch
                                        checked={smartLockerSwitch}
                                        onChange={checked => setSmartLockerSwitch(checked)}
                                        size="middle"
                                    />
                                </SwitchContainer>
                            </Col>

                            <Col xs={24} sm={24} md={8}>
                                <SwitchContainer>
                                    <span style={{fontWeight: "500"}}>Manuel Atama</span>
                                    <Switch
                                        checked={isAllocateClosed}
                                        onChange={checked => setIsAllocateClosed(checked)}
                                        size="middle"
                                    />
                                </SwitchContainer>
                            </Col>

                            <Col xs={24} sm={24} md={8}>
                                <SwitchContainer>
                                    <span style={{fontWeight: "500"}}>
                                        {t(`fields.testUser`, {ns: "common"})}
                                    </span>
                                    <Switch
                                        checked={isTestUser}
                                        onChange={checked => setIsTestUser(checked)}
                                        size="middle"
                                    />
                                </SwitchContainer>
                            </Col>
                            <Col xs={24} sm={24} md={8}>
                                <SwitchContainer>
                                    <span style={{fontWeight: "500"}}>
                                        Havuz Yoğunluk Bildirimi
                                    </span>
                                    <Switch
                                        checked={isPoolIntensityAlertActive}
                                        onChange={checked => setIsPoolIntensityAlertActive(checked)}
                                        size="middle"
                                    />
                                </SwitchContainer>
                            </Col>

                            <Col xs={24} sm={24} md={8}>
                                <SwitchContainer>
                                    <span style={{fontWeight: "500"}}>
                                        Nakitler İade Edilsin Mi?
                                    </span>
                                    <Switch
                                        checked={addReturnProduct}
                                        onChange={checked => setAddReturnProduct(checked)}
                                        size="middle"
                                    />
                                </SwitchContainer>
                            </Col>
                            <Col xs={24} sm={24} md={8}>
                                <SwitchContainer>
                                    <span style={{fontWeight: "500"}}>
                                        En Eski Siparişi Alma Zorunluluğu
                                    </span>
                                    <Switch
                                        checked={jokerLeadIsActive}
                                        onChange={checked => setJokerLeadIsActive(checked)}
                                        size="middle"
                                    />
                                </SwitchContainer>
                            </Col>
                            <Col xs={24} sm={24} md={8}>
                                <SwitchContainer>
                                    <span style={{fontWeight: "500"}}>
                                        Süre (dk){" "}
                                        <Tooltip
                                            title={
                                                '"En Eski Sipariş Seçme Zorunluluğu" ayarı aktif olduğunda kullanılır. Kuryenin seçmesi zorunlu olan en eski siparişin minimum bekleme süresini belirtir.'
                                            }
                                        >
                                            <InfoCircleOutlined style={{color: "#8c8c8c"}} />
                                        </Tooltip>
                                    </span>
                                    <Input
                                        type="number"
                                        min={0}
                                        disabled={!jokerLeadIsActive}
                                        value={jokerLeadThreshold}
                                        onChange={e => {
                                            const raw = e.target.value;
                                            if (raw === "") {
                                                setJokerLeadThreshold("");
                                                return;
                                            }
                                            const value = Number(raw);
                                            setJokerLeadThreshold(value < 0 ? 0 : value);
                                        }}
                                        style={{width: 120}}
                                    />
                                </SwitchContainer>
                            </Col>
                        </Row>
                    </SettingsContainer>
                    <Form.Item
                        label={t(`integrations.commonFields.allowedOrderProviders`, {
                            ns: "integrations"
                        })}
                    >
                        <TagInput
                            tags={tags}
                            setTags={setTags}
                            addTitle={t(`integrations.commonFields.addAllowedOrderProvider`, {
                                ns: "integrations"
                            })}
                            addPlaceHolderText={t(
                                `integrations.commonFields.allowedOrderProvidersPlaceholder`,
                                {
                                    ns: "integrations"
                                }
                            )}
                        ></TagInput>
                    </Form.Item>
                    <Form.Item
                        label={t("fields.location", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `location`) && getIn(touched, `location`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `location`) && getIn(touched, `location`)
                                ? getIn(errors, `location`)
                                : null
                        }
                    >
                        <Row type="flex" gutter={16} justify="center">
                            <Col>{`${t("fields.latitude", {
                                ns: "common"
                            })}: ${values.location?.latitude.toFixed(5)}`}</Col>
                            <Col>{`${t("fields.longitude", {
                                ns: "common"
                            })}: ${values.location?.longitude.toFixed(5)}`}</Col>
                        </Row>
                        <SingleMarkerMap
                            center={{
                                lat: values.location?.latitude,
                                lng: values.location?.longitude
                            }}
                            onClick={handleMapClick}
                            onExternalLink={handleLocationFromLink}
                            previewCenter={previewLocation}
                            previewZoom={previewLocation?.zoom}
                            height="400px"
                            onRecenter={() => setPreviewLocation(null)}
                            addExternalInput
                            icon={<BankOutlined />}
                        />
                    </Form.Item>
                    <Row>
                        <Col span={24}>
                            <LocationFieldsHint>
                                <InfoCircleOutlined className="hint-icon" />
                                <span>
                                    {isResolving
                                        ? "Adres bilgileri alınıyor..."
                                        : "Haritadan konum seçildiğinde il, ilçe ve mahalle bilgileri otomatik olarak alınacak. İl, ilçe ve mahalle elle seçildiğinde harita yalnızca o bölgeye yakınlaşır, konum değişmez; konumu haritaya tıklayarak pinleyebilir veya sağ alttaki butonla pinli konuma dönebilirsiniz."}
                                </span>
                            </LocationFieldsHint>
                        </Col>
                    </Row>
                    <Row type="flex" justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("fields.city", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `city`) && getIn(touched, `city`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `city`) && getIn(touched, `city`)
                                        ? getIn(errors, `city`)
                                        : null
                                }
                            >
                                <CityFilter
                                    showAllOption={false}
                                    showLabel={false}
                                    value={values.city}
                                    allowClear
                                    onChange={v => {
                                        allowForwardGeocodeRef.current = true;
                                        isManualAddressRef.current = true;
                                        setFieldValue("city", v);
                                        setFieldValue("county", "");
                                        setFieldValue("district", "");
                                        setFieldValue("zoneId", null);
                                        setDistrictId(null);
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("fields.county", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `county`) && getIn(touched, `county`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `county`) && getIn(touched, `county`)
                                        ? getIn(errors, `county`)
                                        : null
                                }
                            >
                                <DistrictFilter
                                    allowClear
                                    showAllOption={false}
                                    showLabel={false}
                                    showSearch
                                    value={values.county}
                                    onChange={async v => {
                                        allowForwardGeocodeRef.current = true;
                                        isManualAddressRef.current = true;
                                        setFieldValue("county", v);
                                        setFieldValue("district", "");
                                        setFieldValue("zoneId", null);
                                        const foundDistrictId = await resolveDistrictId({
                                            city: values.city,
                                            county: v
                                        });
                                        setDistrictId(foundDistrictId);
                                    }}
                                    disabled={!values?.city}
                                    city={values?.city}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        label={t("fields.neighborhood", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `district`) && getIn(touched, `district`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `district`) && getIn(touched, `district`)
                                ? getIn(errors, `district`)
                                : null
                        }
                    >
                        <DynamicNeighborhoodFilter
                            showLabel={false}
                            districtId={districtId}
                            valueKey="name"
                            value={values.district}
                            onChange={v => {
                                allowForwardGeocodeRef.current = true;
                                isManualAddressRef.current = true;
                                setFieldValue("district", v);
                            }}
                            disabled={!districtId}
                            allowClear
                        />
                    </Form.Item>
                    <Form.Item
                        label={t("fields.subCompanyCode", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `subCompanyCode`) && getIn(touched, `subCompanyCode`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `subCompanyCode`) && getIn(touched, `subCompanyCode`)
                                ? getIn(errors, `subCompanyCode`)
                                : null
                        }
                    >
                        <Row>
                            <Input
                                disabled
                                value={`${companyCode}`}
                                style={{borderRightWidth: 1, width: 60}}
                            />
                            <Input
                                disabled
                                value={`${companyCityCode}`}
                                style={{borderLeftWidth: 0, width: 40}}
                            />
                            <Input
                                disabled
                                name={"subCompanyCode"}
                                data-testid="subCompanyCode"
                                value={values.subCompanyCode}
                                onChange={handleChange}
                                style={{borderLeftWidth: 0, width: 80}}
                            />
                        </Row>
                    </Form.Item>
                </FormikProvider>
                <Row type="flex">
                    <CustomButton
                        loading={isSubmitting}
                        type="primary"
                        htmlType="submit"
                        disabled={isSubmitting}
                    >
                        {t("actions.save", {ns: "common"})}
                    </CustomButton>
                </Row>
            </Form>
            <Modal
                title="Çalışma Tipi Değişikliği"
                visible={showWorkTypeChangeModal}
                onOk={handleConfirmWorkTypeChange}
                onCancel={handleCancelWorkTypeChange}
                okText="Onayla"
                cancelText="İptal"
                okButtonProps={{loading: isSubmitting}}
                cancelButtonProps={{loading: isSubmitting}}
            >
                <div>
                    <p>
                        Alt firmanın çalışma tipini değiştirdiniz.
                        <br />
                        <strong style={{color: "#c9372c"}}>
                            Değişikliği onaylamanız halinde aktif sözleşme geçerliliğini
                            yitirecektir.
                        </strong>{" "}
                        <br />
                        Yönlendirileceğiniz sözleşme sayfasından yeni sözleşme oluşturmanız
                        gerekmektedir.
                    </p>
                </div>
            </Modal>
        </Col>
    ));
};
